import { useEffect, useState } from "react";
import AdminLayout from "@/components/admin/AdminLayout";
import { supabase } from "@/lib/supabase";
import {
  CurrencyInr,
  Package,
  Users,
  Clock,
  CheckCircle,
  ArrowUpRight,
} from "@phosphor-icons/react";

function Stat({ label, value, icon: Icon, accent = "text-cyan-brand", trend }) {
  return (
    <div
      data-testid={`stat-${label.toLowerCase().replace(/\s+/g, "-")}`}
      className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all"
    >
      <div className="flex items-center justify-between">
        <div className={`h-10 w-10 rounded-xl bg-slate-50 grid place-items-center ${accent}`}>
          <Icon size={20} weight="regular" />
        </div>
        {trend && (
          <span className="inline-flex items-center gap-0.5 text-[11px] text-emerald-600 font-mono-data font-semibold">
            <ArrowUpRight size={11} weight="bold" />
            {trend}
          </span>
        )}
      </div>
      <div className="mt-4 text-2xl md:text-3xl font-display font-bold text-navy-deep tabular-nums">
        {value}
      </div>
      <div className="text-[12px] text-slate-500 mt-0.5">{label}</div>
    </div>
  );
}

export default function AdminDashboard() {
  const [stats, setStats] = useState({
    revenue: 0,
    ordersToday: 0,
    pending: 0,
    delivered: 0,
    customers: 0,
    lowStock: 0,
  });
  const [recentOrders, setRecentOrders] = useState([]);
  const [recentCustomers, setRecentCustomers] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      setLoading(true);

      // Orders + revenue
      const { data: orders } = await supabase
        .from("orders")
        .select("*")
        .order("created_at", { ascending: false })
        .limit(500);

      const list = orders || [];
      const revenue = list.reduce((s, o) => s + Number(o.total || o.amount || 0), 0);
      const today = new Date().toISOString().slice(0, 10);
      const ordersToday = list.filter(
        (o) => (o.created_at || "").slice(0, 10) === today,
      ).length;
      const pending = list.filter((o) =>
        ["new", "processing", "pending"].includes((o.order_status || o.status || "").toLowerCase()),
      ).length;
      const delivered = list.filter((o) =>
        ["delivered", "completed", "paid", "fulfilled"].includes(
          (o.order_status || o.status || "").toLowerCase(),
        ),
      ).length;

      // Customers count
      const { count: customersCount } = await supabase
        .from("profiles")
        .select("id", { count: "exact", head: true })
        .eq("role", "customer");

      // Low stock
      let lowStock = 0;
      try {
        const { count } = await supabase
          .from("products")
          .select("id", { count: "exact", head: true })
          .lt("stock", 10);
        lowStock = count || 0;
      } catch {
        /* stock column may not exist */
      }

      // Recent customers
      const { data: custs } = await supabase
        .from("profiles")
        .select("id, email, full_name, created_at, role")
        .eq("role", "customer")
        .order("created_at", { ascending: false })
        .limit(5);

      setStats({
        revenue,
        ordersToday,
        pending,
        delivered,
        customers: customersCount || 0,
        lowStock,
      });
      setRecentOrders(list.slice(0, 5));
      setRecentCustomers(custs || []);
      setLoading(false);
    })();
  }, []);

  const fmt = (n) => `₹${Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;

  return (
    <AdminLayout title="Dashboard">
      {/* Stat grid */}
      <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4">
        <Stat label="Total Revenue" value={loading ? "…" : fmt(stats.revenue)} icon={CurrencyInr} accent="text-emerald-600" trend="+12%" />
        <Stat label="Orders Today" value={loading ? "…" : stats.ordersToday} icon={Package} />
        <Stat label="Pending" value={loading ? "…" : stats.pending} icon={Clock} accent="text-amber-500" />
        <Stat label="Delivered" value={loading ? "…" : stats.delivered} icon={CheckCircle} accent="text-emerald-600" />
        <Stat label="Customers" value={loading ? "…" : stats.customers} icon={Users} />
        <Stat label="Low Stock" value={loading ? "…" : stats.lowStock} icon={Package} accent="text-red-500" />
      </div>

      {/* Two-column: latest orders + latest customers */}
      <div className="mt-8 grid lg:grid-cols-3 gap-5">
        <div className="lg:col-span-2 rounded-2xl border border-slate-200 bg-white overflow-hidden">
          <div className="px-5 py-4 border-b border-slate-200 flex items-center justify-between">
            <h3 className="font-display font-bold text-navy-deep">Latest Orders</h3>
            <a href="/admin/orders" className="text-[12px] text-cyan-brand hover:underline font-semibold">
              View all →
            </a>
          </div>
          {loading ? (
            <div className="p-6 space-y-3">
              {[0, 1, 2].map((i) => (
                <div key={i} className="h-12 rounded-xl bg-slate-100 animate-pulse" />
              ))}
            </div>
          ) : recentOrders.length === 0 ? (
            <div className="p-8 text-center text-sm text-slate-500">No orders yet.</div>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-[11px] uppercase tracking-widest text-slate-500 font-mono-data">
                <tr>
                  <th className="text-left px-5 py-3 font-semibold">Order</th>
                  <th className="text-left px-5 py-3 font-semibold">Customer</th>
                  <th className="text-left px-5 py-3 font-semibold">Total</th>
                  <th className="text-left px-5 py-3 font-semibold">Status</th>
                </tr>
              </thead>
              <tbody>
                {recentOrders.map((o) => (
                  <tr key={o.id} className="border-t border-slate-100">
                    <td className="px-5 py-3 font-mono-data text-[12px] text-navy-deep">
                      #{(o.order_number || o.id || "").toString().slice(0, 8)}
                    </td>
                    <td className="px-5 py-3 text-navy-deep">{o.email || o.customer_email || "—"}</td>
                    <td className="px-5 py-3 font-semibold text-navy-deep tabular-nums">
                      {fmt(o.total || o.amount || 0)}
                    </td>
                    <td className="px-5 py-3">
                      <span className="text-[10.5px] uppercase tracking-[0.14em] font-mono-data font-semibold px-2 py-1 rounded-full bg-slate-100 text-navy-deep">
                        {o.order_status || o.status || "new"}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden">
          <div className="px-5 py-4 border-b border-slate-200 flex items-center justify-between">
            <h3 className="font-display font-bold text-navy-deep">Latest Customers</h3>
            <a href="/admin/customers" className="text-[12px] text-cyan-brand hover:underline font-semibold">
              View all →
            </a>
          </div>
          {loading ? (
            <div className="p-6 space-y-3">
              {[0, 1, 2].map((i) => (
                <div key={i} className="h-12 rounded-xl bg-slate-100 animate-pulse" />
              ))}
            </div>
          ) : recentCustomers.length === 0 ? (
            <div className="p-8 text-center text-sm text-slate-500">No customers yet.</div>
          ) : (
            <div className="divide-y divide-slate-100">
              {recentCustomers.map((c) => (
                <div key={c.id} className="flex items-center gap-3 px-5 py-3">
                  <div className="h-9 w-9 rounded-full bg-cta-gradient grid place-items-center text-white font-display font-bold text-[13px] uppercase">
                    {(c.full_name || c.email || "?")[0]}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="text-[13.5px] font-semibold text-navy-deep truncate">
                      {c.full_name || c.email?.split("@")[0]}
                    </div>
                    <div className="text-[11px] text-slate-500 truncate">{c.email}</div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </AdminLayout>
  );
}
